<script lang="ts" setup>
  import { nextTick, provide, ref, useSlots, watchEffect } from 'vue';
  import { descriptionContextKey, DescriptionsProps, flattedChildren } from './descriptions';
  import type { IDescriptionItem } from './descriptions';
  defineOptions({
    name: 'IDescriptions',
  });

  const props = defineProps(DescriptionsProps);
  const slots = useSlots();
  const otSpan = ref(0);
  const otRowSpan = ref(0);

  provide(descriptionContextKey, props);

  watchEffect(() => {
    nextTick().then(() => {
      if (slots.default) {
        const list = flattedChildren(slots.default()).filter((item: any) => item.type?.name === 'IDescriptionItem');
        const rows = getRows(list, props.columns);
        const lastRow = rows[rows.length - 1];
        const spanTotal = lastRow.reduce((acc, cur) => acc + (cur.props?.span || 1), 0);
        otSpan.value = props.columns - spanTotal;
        otRowSpan.value = Math.max(0, ...lastRow.map((item: any) => item.props?.rowSpan || 1));
      }
    });
  });

  /**
   * 获取行数据
   * @param list 列表
   * @param columns 列数
   */
  function getRows(list: IDescriptionItem[], columns: number): IDescriptionItem[][] {
    const rows: IDescriptionItem[][] = [];
    let currentRow: IDescriptionItem[] = [];
    let currentColumn = 0;
    list.forEach((item: IDescriptionItem) => {
      const span = item.props?.span || 1; // 列数
      const rowSpan = item.props?.rowSpan || 1; // 行数

      // 如果当前行已满，则创建新行
      if (currentColumn + span > columns) {
        rows.push(currentRow);
        currentRow = [];
        currentColumn = 0;
      }

      // 将子组件添加到当前行
      currentRow.push(item);
      currentColumn += span;

      // 处理 rowSpan
      for (let i = 1; i < rowSpan; i++) {
        if (rows.length < rows.length + i) {
          rows.push([]);
        }
        rows[rows.length - 1 + i].push({ ...item, _isPlaceholder: true });
      }
    });
    // 添加最后一行
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }
    return rows;
  }
</script>

<template>
  <div
    class="i-descriptions"
    :class="{ 'i-descriptions__border': border }"
    :style="{
      'grid-template-columns': `repeat(${columns}, 1fr)`,
    }"
  >
    <slot />
    <div
      v-if="otSpan > 0"
      class="i-descriptions-item"
      :class="[`i-descriptions-item__${size || 'default'}`]"
      :style="{
        gridColumnStart: `span ${otSpan}`,
        gridRowStart: `span ${otRowSpan}`,
      }"
    />
  </div>
</template>
